import { StorageData, StorageError, StorageKey, StoredAssessment, UserPreferences, StoredEmail } from '../types';

const STORAGE_VERSION = '1.0.0';
const DEFAULT_PREFERENCES: UserPreferences = {
  theme: 'auto',
  language: 'en',
  notifications: true,
  dataRetention: 365
};

/**
 * Check if localStorage is available
 */
export function isStorageAvailable(): boolean {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

/**
 * Get data from localStorage with error handling
 */
export function getStorageData<T>(key: StorageKey, defaultValue: T): T {
  if (!isStorageAvailable()) {
    console.warn('localStorage not available, using default value');
    return defaultValue;
  }

  try {
    const item = localStorage.getItem(key);
    if (!item) return defaultValue;
    
    const parsed = JSON.parse(item);
    
    // Handle date deserialization
    if (parsed && typeof parsed === 'object') {
      return deserializeDates(parsed) as T;
    }
    
    return parsed;
  } catch (error) {
    console.error(`Error reading from localStorage (${key}):`, error);
    return defaultValue;
  }
}

/**
 * Set data in localStorage with error handling
 */
export function setStorageData<T>(key: StorageKey, data: T): StorageError | null {
  if (!isStorageAvailable()) {
    return {
      type: 'not_available',
      message: 'localStorage is not available',
      recoverable: false
    };
  }

  try {
    const serialized = JSON.stringify(data);
    localStorage.setItem(key, serialized);
    return null;
  } catch (error) {
    if (error instanceof Error && error.name === 'QuotaExceededError') {
      // Try to free up space by removing old data
      try {
        const currentData = getStorageData<StorageData>('ayurvedic_quiz_data', {
          assessmentHistory: [],
          preferences: DEFAULT_PREFERENCES,
          version: STORAGE_VERSION,
          emails: []
        });
        
        // Remove oldest assessments to free space
        if (currentData.assessmentHistory.length > 5) {
          const trimmedHistory = currentData.assessmentHistory.slice(0, 5);
          const trimmedData = { ...currentData, assessmentHistory: trimmedHistory };
          const serialized = JSON.stringify(trimmedData);
          localStorage.setItem(key, serialized);
          return null;
        }
      } catch (cleanupError) {
        console.error('Failed to cleanup storage:', cleanupError);
      }
      
      return {
        type: 'quota_exceeded',
        message: 'Storage quota exceeded. Please clear some browser data.',
        recoverable: true
      };
    }
    
    return {
      type: 'unknown',
      message: error instanceof Error ? error.message : 'Unknown storage error',
      recoverable: false
    };
  }
}

/**
 * Remove data from localStorage
 */
export function removeStorageData(key: StorageKey): void {
  if (!isStorageAvailable()) return;
  
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing from localStorage (${key}):`, error);
  }
}

/**
 * Get all assessment history
 */
export function getAssessmentHistory(): StoredAssessment[] {
  const data = getStorageData<StorageData>('ayurvedic_quiz_data', {
    assessmentHistory: [],
    preferences: DEFAULT_PREFERENCES,
    version: STORAGE_VERSION,
    emails: []
  });
  
  return data.assessmentHistory;
}

/**
 * Save assessment result to history
 */
export function saveAssessmentResult(result: StoredAssessment): StorageError | null {
  const currentData = getStorageData<StorageData>('ayurvedic_quiz_data', {
    assessmentHistory: [],
    preferences: DEFAULT_PREFERENCES,
    version: STORAGE_VERSION,
    emails: []
  });
  
  // Add new result to history
  const updatedHistory = [result, ...currentData.assessmentHistory];
  
  // Apply data retention policy
  const retentionDays = currentData.preferences.dataRetention;
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - retentionDays);
  
  const filteredHistory = updatedHistory.filter(assessment => 
    new Date(assessment.timestamp) > cutoffDate
  );
  
  const updatedData: StorageData = {
    ...currentData,
    assessmentHistory: filteredHistory
  };
  
  return setStorageData('ayurvedic_quiz_data', updatedData);
}

/**
 * Save user email for future access
 */
export function saveUserEmail(email: string): StorageError | null {
  const currentData = getStorageData<StorageData>('ayurvedic_quiz_data', {
    assessmentHistory: [],
    preferences: DEFAULT_PREFERENCES,
    version: STORAGE_VERSION,
    emails: []
  });
  
  // Check if email already exists
  const existingEmailIndex = currentData.emails.findIndex(e => e.email === email);
  
  if (existingEmailIndex >= 0) {
    // Update existing email record
    currentData.emails[existingEmailIndex].lastAssessmentDate = new Date();
    currentData.emails[existingEmailIndex].assessmentCount += 1;
  } else {
    // Add new email record
    const newEmailRecord: StoredEmail = {
      email,
      timestamp: new Date(),
      assessmentCount: 1,
      lastAssessmentDate: new Date()
    };
    currentData.emails.push(newEmailRecord);
  }
  
  return setStorageData('ayurvedic_quiz_data', currentData);
}

/**
 * Get all stored emails
 */
export function getStoredEmails(): StoredEmail[] {
  const data = getStorageData<StorageData>('ayurvedic_quiz_data', {
    assessmentHistory: [],
    preferences: DEFAULT_PREFERENCES,
    version: STORAGE_VERSION,
    emails: []
  });
  
  return data.emails;
}

/**
 * Delete assessment from history
 */
export function deleteAssessment(assessmentId: string): StorageError | null {
  const currentData = getStorageData<StorageData>('ayurvedic_quiz_data', {
    assessmentHistory: [],
    preferences: DEFAULT_PREFERENCES,
    version: STORAGE_VERSION,
    emails: []
  });
  
  const updatedHistory = currentData.assessmentHistory.filter(
    assessment => assessment.id !== assessmentId
  );
  
  const updatedData: StorageData = {
    ...currentData,
    assessmentHistory: updatedHistory
  };
  
  return setStorageData('ayurvedic_quiz_data', updatedData);
}

/**
 * Clear all stored data
 */
export function clearAllData(): void {
  removeStorageData('ayurvedic_quiz_data');
  removeStorageData('ayurvedic_quiz_current');
  removeStorageData('ayurvedic_quiz_preferences');
}

/**
 * Deserialize dates in objects
 */
function deserializeDates(obj: any): any {
  if (obj === null || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj.map(deserializeDates);
  }
  
  const result: any = {};
  for (const [key, value] of Object.entries(obj)) {
    if (key === 'timestamp' || key.endsWith('Date') || key.endsWith('Time')) {
      result[key] = typeof value === 'string' ? new Date(value) : value;
    } else if (typeof value === 'object') {
      result[key] = deserializeDates(value);
    } else {
      result[key] = value;
    }
  }
  
  return result;
}

/**
 * Get user preferences
 */
export function getUserPreferences(): UserPreferences {
  return getStorageData<UserPreferences>('ayurvedic_quiz_preferences', DEFAULT_PREFERENCES);
}

/**
 * Update user preferences
 */
export function updateUserPreferences(preferences: Partial<UserPreferences>): StorageError | null {
  const current = getUserPreferences();
  const updated = { ...current, ...preferences };
  return setStorageData('ayurvedic_quiz_preferences', updated);
}